import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callsApi, leadScoringSettingsApi } from "./api";
import type { LeadScoreSettings } from "./api";

export function useCalls(params?: {
  status?: string;
  lead_grade?: "hot" | "warm" | "cold";
}) {
  return useQuery({
    queryKey: ["calls", "list", params],
    queryFn: () => callsApi.list(params),
    staleTime: 15_000,
    // Fail fast: a slow/hung backend shouldn't freeze the page behind
    // 3 silent retries × the 30s axios timeout. Surface an error quickly
    // so the user gets a Retry button instead of an endless skeleton.
    retry: 1,
  });
}

export function useCall(id: string | undefined) {
  return useQuery({
    queryKey: ["calls", "detail", id],
    queryFn: () => callsApi.get(id!),
    enabled: !!id,
    staleTime: 30_000,
    // Poll only while a background scoring task is actively running.
    // "pending" is the default on old rows — don't poll those.
    refetchInterval: (q) => {
      const s = q.state.data?.lead_score_status;
      if (s === "processing") return 4_000;
      return false;
    },
  });
}

export function useCallTranscript(id: string | undefined) {
  return useQuery({
    queryKey: ["calls", "transcript", id],
    queryFn: () => callsApi.transcript(id!),
    enabled: !!id,
    staleTime: 60_000,
    retry: false,
  });
}

export function useCallLatency(id: string | undefined) {
  return useQuery({
    queryKey: ["calls", "latency", id],
    queryFn: () => callsApi.latency(id!),
    enabled: !!id,
    staleTime: 60_000,
    retry: false,
  });
}

export function useCallSummary(id: string | undefined) {
  return useQuery({
    queryKey: ["calls", "summary", id],
    queryFn: () => callsApi.summary(id!),
    enabled: !!id,
    // Refetch only while something is actively in flight. "pending" is the
    // default for old rows where no task has been started — don't poll those.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      if (data.summary_status === "processing") return 4_000;
      if (data.crm_push_status === "pending") return 3_000;
      return false;
    },
    staleTime: 15_000,
    retry: false,
  });
}

export function useGenerateCallSummary(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => callsApi.generateSummary(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calls", "summary", id] });
      qc.invalidateQueries({ queryKey: ["calls", "detail", id] });
    },
  });
}

export function usePushCallToCrm(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (integrationId: string) => callsApi.pushToCrm(id, integrationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calls", "summary", id] });
      qc.invalidateQueries({ queryKey: ["calls", "detail", id] });
    },
  });
}

export function useGenerateLeadScore(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => callsApi.generateLeadScore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calls", "detail", id] });
      qc.invalidateQueries({ queryKey: ["calls", "list"] });
    },
  });
}

export function useSendCallFollowup(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => callsApi.sendFollowup(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calls", "detail", id] });
    },
  });
}

export function useLeadScoringSettings() {
  return useQuery({
    queryKey: ["tenant", "lead-scoring-settings"],
    queryFn: leadScoringSettingsApi.get,
    staleTime: 60_000,
  });
}

export function useUpdateLeadScoringSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<LeadScoreSettings>) =>
      leadScoringSettingsApi.update(data),
    onSuccess: (data) => {
      qc.setQueryData(["tenant", "lead-scoring-settings"], data);
    },
  });
}
